import React from 'react';
import PropTypes from 'prop-types';

import {Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from 'views/components';
import {ArrowDownwardOutlined, ArrowUpwardOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import formatters from '../shared/formatters';
import {getPeriod, getThroughDate, OVERVIEW_METRICS, PERIOD_LABEL_EXPRESSIONS, PERIOD_ROLES} from '../shared/periods';

const toneStyles = {
  positive: {color: '#21892C', backgroundColor: '#E3FAE5'},
  negative: {color: '#DA7955', backgroundColor: '#FFF1EC'},
  neutral: {color: '#919191', backgroundColor: '#F2F0EC'},
};

// A subtle left rule opens each new month group so the three periods read as distinct
// bands within the single table.
const GROUP_DIVIDER = '1px solid #EEE9E1';

const groupHeaderSx = {
  borderBottom: 'none',
  verticalAlign: 'bottom',
  pb: 0.5,
};

const subHeaderSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  color: '#A8A49E',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #EAEAEA',
};

const bodyCellSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F2F0EC',
  verticalAlign: 'middle',
  py: 1.25,
};

const labelCellSx = {
  ...bodyCellSx,
  fontWeight: 600,
  color: '#6B6761',
};

const renderVarianceChip = (variance, ariaLabel) => {
  const toneStyle = toneStyles[variance.tone] || toneStyles.neutral;

  return (
    <Chip
      icon={
        variance.direction === 'down' ?
          <ArrowDownwardOutlined sx={{fontSize: '12px !important', color: `${toneStyle.color} !important`}} /> :
          <ArrowUpwardOutlined sx={{fontSize: '12px !important', color: `${toneStyle.color} !important`}} />
      }
      label={variance.label}
      aria-label={ariaLabel}
      sx={{
        'height': 18,
        'backgroundColor': toneStyle.backgroundColor,
        'borderRadius': '20px',
        'color': toneStyle.color,
        'fontSize': '10px',
        'fontWeight': 500,
        '& .MuiChip-label': {px: 0.625},
        '& .MuiChip-icon': {ml: 0.375},
      }}
    />
  );
};

// One unified comparison table: metric labels appear once on the left, and each calendar
// month is a divided column group. Previous/current expose Scheduled + Actual; next shows
// Scheduled only (its actual column is omitted, never rendered as $0). Month-over-month
// variance rides as a small chip beside the scheduled value it describes.
const MonthlyOverviewTable = (props) => {
  const {snapshot} = props;

  const throughDate = getThroughDate(snapshot);

  const groups = React.useMemo(() => {
    return PERIOD_ROLES.map((role) => {
      const period = getPeriod(snapshot, role);

      let note = translate('metrics.ClientMetrics.Overview.FullMonth');

      if (role === 'current') {
        note = throughDate ?
          translate('metrics.ClientMetrics.Overview.ActualThrough', {date: formatters.formatThroughDate(throughDate)}) :
          translate('metrics.ClientMetrics.Overview.FullMonth');
      } else if (role === 'next') {
        note = translate('metrics.ClientMetrics.Overview.ScheduledOnly');
      }

      return {
        role,
        labelExpression: PERIOD_LABEL_EXPRESSIONS[role],
        monthLabel: formatters.formatMonthLabel(period?.month),
        note,
        scheduled: period?.scheduled || null,
        actual: period?.actual || null,
        showActual: role !== 'next',
      };
    });
  }, [snapshot, throughDate]);

  const rows = React.useMemo(() => {
    return OVERVIEW_METRICS
        .map((metric) => {
          const cells = groups.map((group, index) => {
            const scheduledValue = group.scheduled?.[metric.key];
            const actualValue = group.actual?.[metric.key];
            // Month-over-month change in the scheduled forecast: each month's scheduled
            // value against the prior month's scheduled value (current vs previous, next vs
            // current). The first month has no prior, so no delta. Shown on the scheduled
            // figure.
            const priorScheduled = index > 0 ? groups[index - 1].scheduled : null;
            const scheduledDelta = priorScheduled ?
              formatters.buildVariance(priorScheduled[metric.key], scheduledValue, {kind: metric.kind, goodDirection: metric.goodDirection}) :
              null;

            return {
              role: group.role,
              showActual: group.showActual,
              scheduledText: formatters.formatByKind(scheduledValue, metric.kind),
              actualText: formatters.formatByKind(actualValue, metric.kind),
              scheduledDelta,
              hasAnyValue: !formatters.isMissingValue(scheduledValue) || (group.showActual && !formatters.isMissingValue(actualValue)),
            };
          });

          return {
            key: metric.key,
            label: translate(metric.labelExpression),
            optional: Boolean(metric.optional),
            cells,
          };
        })
        // Optional rows (e.g. expenses, which only exist on the actual side) drop out
        // entirely when no period carries a value, rather than showing a row of dashes.
        .filter((row) => !row.optional || row.cells.some((cell) => cell.hasAnyValue));
  }, [groups]);

  const columnCount = 1 + groups.reduce((total, group) => total + (group.showActual ? 2 : 1), 0);

  return (
    <TableContainer sx={{overflowX: 'auto'}}>
      <Table size='small' sx={{minWidth: 760}}>
        <TableHead>
          <TableRow>
            <TableCell sx={groupHeaderSx} />
            {
              groups.map((group) => (
                <TableCell
                  key={group.role}
                  colSpan={group.showActual ? 2 : 1}
                  align='left'
                  sx={{...groupHeaderSx, borderLeft: GROUP_DIVIDER, pl: 1.5}}
                >
                  <Typography sx={{fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', color: '#A8A49E'}}>
                    <Translate expression={group.labelExpression} />
                  </Typography>
                  <Typography sx={{fontSize: '13px', fontWeight: 600, lineHeight: '17px', color: '#1A1916'}}>
                    {group.monthLabel}
                  </Typography>
                  <Typography sx={{fontSize: '10px', lineHeight: '13px', color: '#A8A49E'}}>
                    {group.note}
                  </Typography>
                </TableCell>
              ))
            }
          </TableRow>
          <TableRow>
            <TableCell align='left' sx={subHeaderSx}>
              <Translate expression='metrics.ClientMetrics.Overview.Metric' />
            </TableCell>
            {
              groups.map((group) => (
                <React.Fragment key={group.role}>
                  <TableCell align='right' sx={{...subHeaderSx, borderLeft: GROUP_DIVIDER}}>
                    <Translate expression='metrics.ClientMetrics.Overview.Scheduled' />
                  </TableCell>
                  {
                    group.showActual &&
                    <TableCell align='right' sx={subHeaderSx}>
                      <Translate expression='metrics.ClientMetrics.Overview.Actual' />
                    </TableCell>
                  }
                </React.Fragment>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {
            rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell align='left' sx={labelCellSx}>
                  {row.label}
                </TableCell>
                {
                  row.cells.map((cell) => (
                    <React.Fragment key={cell.role}>
                      <TableCell align='right' sx={{...bodyCellSx, borderLeft: GROUP_DIVIDER}}>
                        <Stack direction='row' alignItems='center' justifyContent='flex-end' spacing={0.75}>
                          {
                            cell.scheduledDelta &&
                            renderVarianceChip(
                                cell.scheduledDelta,
                                translate('metrics.ClientMetrics.Overview.VarianceLabel', {metric: row.label, value: cell.scheduledDelta.label}),
                            )
                          }
                          <span>{cell.scheduledText}</span>
                        </Stack>
                      </TableCell>
                      {
                        cell.showActual &&
                        <TableCell align='right' sx={bodyCellSx}>
                          {cell.actualText}
                        </TableCell>
                      }
                    </React.Fragment>
                  ))
                }
              </TableRow>
            ))
          }
          {
            !rows.length &&
            <TableRow>
              <TableCell colSpan={columnCount} sx={{...bodyCellSx, color: '#A8A49E', textAlign: 'center'}}>
                {formatters.unavailableText()}
              </TableCell>
            </TableRow>
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

MonthlyOverviewTable.propTypes = {
  snapshot: PropTypes.object,
};

export default React.memo(MonthlyOverviewTable);
